import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { UlmPasswordField } from "@components/ulm/ulm-password-field";
import {
  MIN_PASSWORD_LENGTH,
  PasswordValidationChecker,
  TextField,
} from "@flat-ui";
import { Box } from "@mui-wrapper";
import { fieldSpacingSx } from "@styles/common-styles";
import { useState } from "react";
import { SignupFormProps } from "@components/signup/signup-form/signup-form-types";

/**
 * Renders the signup form with fields for first name, last name and password
 * for users that selected the email/password authentication method.
 */
export function SignupForm({
  form,
  setForm,
  errors,
  setErrors,
  setIsPasswordValid,
}: SignupFormProps): JSX.Element {
  const [isPasswordInputActive, setIsPasswordInputActive] =
    useState<boolean>(false);
  const isPasswordCheckerVisible =
    isPasswordInputActive || errors.isPasswordError;

  function onChangeFirstName(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    if (errors.isFirstNameError) {
      setErrors((prevErrors) => ({ ...prevErrors, isFirstNameError: false }));
    }
    setForm((prevForm) => ({ ...prevForm, firstName: event.target.value }));
  }

  function onChangeLastName(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    if (errors.isLastNameError) {
      setErrors((prevErrors) => ({ ...prevErrors, isLastNameError: false }));
    }
    setForm((prevForm) => ({ ...prevForm, lastName: event.target.value }));
  }

  function onChangePassword(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    if (errors.isPasswordError) {
      setErrors((prevErrors) => ({ ...prevErrors, isPasswordError: false }));
    }
    setForm((prevForm) => ({ ...prevForm, password: event.target.value }));
  }

  return (
    <>
      <UlmInputLabel title="First name" />
      <TextField
        fullWidth
        controlStyle={fieldSpacingSx}
        text={form.firstName}
        type="text"
        placeholder="Enter your first name"
        error={
          errors.isFirstNameError ? "Please provide your first name" : undefined
        }
        InputProps={{
          name: "firstName",
          autoComplete: "given-name",
          onChange: onChangeFirstName,
        }}
      />

      <UlmInputLabel title="Last name" />
      <TextField
        fullWidth
        controlStyle={fieldSpacingSx}
        text={form.lastName}
        type="text"
        placeholder="Enter your last name"
        error={
          errors.isLastNameError ? "Please provide your last name" : undefined
        }
        InputProps={{
          name: "lastName",
          autoComplete: "family-name",
          onChange: onChangeLastName,
        }}
      />

      <UlmPasswordField
        value={form.password}
        placeholder={`Enter at least ${MIN_PASSWORD_LENGTH} characters`}
        onChange={onChangePassword}
        hasError={errors.isPasswordError}
        helperText={
          errors.isPasswordError ? "Please provide a strong password" : ""
        }
        onFocus={() => setIsPasswordInputActive(true)}
        onBlur={() => setIsPasswordInputActive(false)}
      />
      {isPasswordCheckerVisible && (
        <Box
          sx={{
            width: "100%",
          }}
        >
          <PasswordValidationChecker
            password={form.password}
            onValidationChange={setIsPasswordValid}
          />
        </Box>
      )}
    </>
  );
}
